<template>
  <div class="home">
    <header class="header">
      <h2>web-sdk 功能测试台</h2>
      <el-button type="primary" size="large" @click="go('dashboard')"
        >进入监控后台</el-button
      >
    </header>

    <div class="grid">
      <el-card
        v-for="item in cases"
        :key="item.name"
        class="case-card"
        shadow="hover"
      >
        <template #header>
          <span class="case-name">{{ item.name }}</span>
        </template>
        <p class="case-desc">{{ item.desc }}</p>
        <el-button type="primary" plain @click="run(item)">触发</el-button>
      </el-card>
    </div>

    <el-card class="auto-card" shadow="never">
      <template #header>
        <span class="case-name">自动采集项（无需操作）</span>
      </template>
      <p v-for="text in autoItems" :key="text" class="auto-item">{{ text }}</p>
    </el-card>
  </div>
</template>

<script setup lang="ts">
import { inject } from "vue";
import webSdk from "@xyz-sdk/core";
import { ElMessage } from "element-plus";
import { throwTestError } from "../utils/testError";

const go = inject<(page: "home" | "dashboard") => void>("go", () => {});

const now = () => new Date().toLocaleTimeString();

const cases = [
  {
    name: "代码错误",
    desc: "抛出未捕获 Error，由 window error 监听采集，type=error",
    trigger() {
      setTimeout(() => {
        throw new Error(`测试代码错误 ${now()}`);
      });
    },
  },
  {
    name: "同步错误",
    desc: "抛出未捕获 Error，由 window error 监听采集，type=error",
    trigger() {
      throwTestError(`测试同步错误 ${now()}`);
    },
  },
  {
    name: "Promise 异常",
    desc: "未处理的 Promise.reject，触发 unhandledrejection 上报",
    trigger() {
      setTimeout(() => {
        Promise.reject(new Error(`测试Promise异常 ${now()}`));
      });
    },
  },
  {
    name: "fetch 接口异常",
    desc: "请求一个不存在的接口，返回 404 被判定为接口错误",
    trigger() {
      fetch(`/api/not-found-fetch-${Date.now()}`);
    },
  },
  {
    name: "xhr 接口异常",
    desc: "通过 XMLHttpRequest 请求 404 接口，验证 XHR 重写链路",
    trigger() {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", `/api/not-found-xhr-${Date.now()}`);
      xhr.send();
    },
  },
  {
    name: "资源加载错误",
    desc: "加载一张不存在的图片，触发 resource 类型资源错误",
    trigger() {
      const img = new Image();
      img.src = `/missing-${Date.now()}.png`;
    },
  },
  {
    name: "路由变化",
    desc: "pushState 切换路由，记录到行为栈（随下一条错误上报可见）",
    trigger() {
      history.pushState({}, "", `?test=${Date.now()}`);
    },
  },
  {
    name: "自定义日志",
    desc: "调用 webSdk.log 主动上报 custom 类型业务事件",
    trigger() {
      webSdk.log({
        message: `自定义业务日志 ${now()}`,
        error: new Error("自定义错误详情"),
      });
    },
  },
];

const autoItems = [
  "性能指标：页面加载后自动采集 FCP/LCP/CLS/TTFB、长任务、资源列表、内存",
  "白屏检测：monitorWhiteScreen 已开启，页面加载后自动采样检测",
  "录屏：触发任意错误后，约 10 秒（一个录屏时间片）自动上报 recordScreen 数据，可在监控后台播放",
];

function run(item: (typeof cases)[number]) {
  ElMessage.success(`已触发「${item.name}」，稍后到监控后台查看`);
  setTimeout(() => {
    item.trigger();
  }, 100);
}
</script>

<style scoped>
.home {
  max-width: 1080px;
  margin: 0 auto;
  padding: 24px 20px 48px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header h2 {
  margin: 0;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.case-name {
  font-weight: 600;
}

.case-desc {
  color: #666;
  font-size: 13px;
  min-height: 40px;
  margin: 0 0 12px;
}

.auto-item {
  margin: 6px 0;
  color: #666;
  font-size: 13px;
}
</style>
